import type { ReservationModeButton } from '@iheartjane/cart';
import { ReservationModeToggle } from '@iheartjane/cart';
import { setBagPickupMode, useBagPickupMode } from '@iheartjane/cart/utils';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box } from '@iheartjane/reefer';
import type {
  ReservationMode,
  ShopperReservationMode,
} from '@iheartjane/types';
import { useEffect, useRef } from 'react';

import { EMPTY_V3_CART } from '@jane/monolith/components/cart/cartFacade/emptyV3Cart';
import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import {
  resolveModeChange,
  resolveSelectedMode,
} from '@jane/shared-ecomm/pages';
import { useEcommApp } from '@jane/shared/providers';

import styles from './cartDrawerToggle.module.css';

interface CartDrawerToggleProps {
  curbsideLabel: string;
  deliveryAddress?: string;
  deliveryLabel: string;
  hasCurbsideSlots: boolean;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  isAcceptingDeliveryOrders: boolean;
  isLoading: boolean;
  modesToRender: ShopperReservationMode[];
  pickupLabel: string;
  refetchCurbside: () => void;
  refetchDelivery: () => void;
  refetchPickup: () => void;
  slotOpenModes: ShopperReservationMode[];
  slotsSettled: boolean;
}

export const CartDrawerToggle = ({
  curbsideLabel,
  deliveryAddress,
  deliveryLabel,
  hasCurbsideSlots,
  hasDeliverySlots,
  hasPickupSlots,
  isAcceptingDeliveryOrders,
  isLoading,
  modesToRender,
  pickupLabel,
  refetchCurbside,
  refetchDelivery,
  refetchPickup,
  slotOpenModes,
  slotsSettled,
}: CartDrawerToggleProps) => {
  const hasSeededCurbsideRef = useRef(false);
  const hasRealignedRef = useRef(false);
  const { appMode } = useEcommApp();
  const isHeadless = ['bloom', 'headless'].includes(appMode);
  const { updateReservationMode } = useCartActions();
  // The mode comes from the facade so the controlled toggle converges with the
  // rest of the drawer. A null cart coalesces onto the same empty cart the
  // slice holds before one exists, so the mount realign behaves as before.
  const { cart, hasLoaded } = useCartState();
  const { reservation_mode: currentReservationMode } = cart ?? EMPTY_V3_CART;
  const bagPickupMode = useBagPickupMode();
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;

  const labelFor = (mode: ShopperReservationMode) =>
    mode === 'delivery'
      ? deliveryLabel
      : mode === 'pickup'
        ? pickupLabel
        : curbsideLabel;

  const handleReservationModeToggle = (reservationMode: ReservationMode) => {
    updateReservationMode({ reservationMode });
  };

  const handleModeChange = (mode: ShopperReservationMode) => {
    if (mode === 'delivery') {
      handleReservationModeToggle('delivery');
      // Reset the pickup sub-mode so a prior 'curbside' choice doesn't re-seed
      // the checkout form if the shopper switches back.
      if (curbsideFlagOn) setBagPickupMode('pickup');
      if (deliveryAddress) {
        refetchDelivery();
      } else {
        refetchPickup();
      }
    } else {
      // Curbside and pickup both submit as 'pickup'; bagPickupMode distinguishes.
      handleReservationModeToggle('pickup');
      if (curbsideFlagOn) setBagPickupMode(mode);
      if (mode === 'curbside') {
        refetchCurbside();
      } else {
        refetchPickup();
      }
    }
  };

  // Clamped to modesToRender, drives render.
  const selectedMode = resolveSelectedMode(
    currentReservationMode,
    bagPickupMode,
    modesToRender
  );

  useEffect(() => {
    // Realign once, on the first settled load: an unsettled cart would clamp
    // `modesToRender` against the empty-cart placeholder.
    if (!hasLoaded) return;

    if (curbsideFlagOn) {
      if (hasSeededCurbsideRef.current) return;
      if (!slotsSettled) return;
      hasSeededCurbsideRef.current = true;

      const modeChange = resolveModeChange(
        currentReservationMode,
        bagPickupMode,
        slotOpenModes,
        modesToRender
      );
      if (!modeChange) return;

      const wireMode: ReservationMode =
        modeChange === 'delivery' ? 'delivery' : 'pickup';
      if (wireMode !== currentReservationMode) {
        handleReservationModeToggle(wireMode);
      }
      setBagPickupMode(modeChange === 'curbside' ? 'curbside' : 'pickup');
    } else {
      if (hasRealignedRef.current) return;
      hasRealignedRef.current = true;

      const mode = modesToRender.includes(
        currentReservationMode as ShopperReservationMode
      )
        ? currentReservationMode
        : modesToRender[0];
      if (mode && mode !== currentReservationMode) {
        handleReservationModeToggle(mode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded, curbsideFlagOn, slotsSettled, slotOpenModes, modesToRender]);

  const toggleValue = curbsideFlagOn ? selectedMode : currentReservationMode;
  const isAcceptingDelivery = isAcceptingDeliveryOrders || hasDeliverySlots;

  const modes: ReservationModeButton[] = modesToRender.map((mode) => ({
    closed:
      (mode === 'delivery' && !isAcceptingDelivery) ||
      (mode === 'pickup' && !hasPickupSlots) ||
      (mode === 'curbside' && !hasCurbsideSlots),
    label: labelFor(mode),
    mode,
    selected: mode === toggleValue,
  }));

  return (
    <Box width="100%" mb={isHeadless ? 16 : 0}>
      <ReservationModeToggle
        buttonClass={
          appMode === 'bloom' ? styles['premiumToggleButton'] : undefined
        }
        isLoading={isLoading}
        modes={modes}
        onChange={handleModeChange}
      />
    </Box>
  );
};
